import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Brain, Lock, Mail, TrendingUp, Shield, Eye, EyeOff, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { login, register } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login({ email, password });

    if (result.success) {
      toast.success(i18n.language === 'tr' ? 'Proje Dashboard\'a hoş geldiniz!' : 'Welcome to Project Dashboard!');
      navigate('/');
    } else {
      toast.error(result.error || (i18n.language === 'tr' ? 'Giriş başarısız' : 'Login failed'));
    }
    setLoading(false);
  };

  const handleDemoLogin = async () => {
    setLoading(true);
    
    const loginResult = await login({
      email: 'demo@virtualcfo.com',
      password: 'demo123'
    });

    if (loginResult.success) {
      toast.success(i18n.language === 'tr' ? 'Demo kullanıcı olarak giriş yapıldı!' : 'Logged in as Demo User!');
      navigate('/');
    } else {
      const registerResult = await register({
        email: 'demo@virtualcfo.com',
        password: 'demo123',
        name: 'Demo User',
        company: 'Oracle Cost Control',
        role: 'Project Controller'
      });

      if (registerResult.success) {
        toast.success(i18n.language === 'tr' ? 'Demo hesabı oluşturuldu!' : 'Demo account created!');
        navigate('/');
      } else {
        toast.error(i18n.language === 'tr' ? 'Demo hesabı oluşturulamadı' : 'Failed to create demo account');
      }
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4">
        <Select value={i18n.language} onValueChange={changeLanguage}>
          <SelectTrigger className="w-[140px] bg-white/80 backdrop-blur-sm" data-testid="language-selector">
            <Globe size={16} className="mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="en">
              <span className="flex items-center gap-2">🇬🇧 English</span>
            </SelectItem>
            <SelectItem value="tr">
              <span className="flex items-center gap-2">🇹🇷 Türkçe</span>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="w-full max-w-6xl grid md:grid-cols-2 gap-8 items-center">
        {/* Left Side - Branding */}
        <div className="hidden md:block space-y-6">
          <div className="flex items-center gap-3 mb-8">
            <img 
              src="https://logos-world.net/wp-content/uploads/2020/09/Oracle-Logo.png" 
              alt="Oracle" 
              className="h-12 w-auto"
            />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{t('home.title')}</h1>
              <p className="text-gray-600">AI-Powered Project Intelligence</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-xl">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0">
                <TrendingUp size={24} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{t('login.feature1Title')}</h3>
                <p className="text-sm text-gray-600">{t('login.feature1Desc')}</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-xl">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-400 to-indigo-600 flex items-center justify-center flex-shrink-0">
                <Brain size={24} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{t('login.feature2Title')}</h3>
                <p className="text-sm text-gray-600">{t('login.feature2Desc')}</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-white/60 backdrop-blur-sm rounded-xl">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-orange-400 to-red-600 flex items-center justify-center flex-shrink-0">
                <Shield size={24} className="text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{t('login.feature3Title')}</h3>
                <p className="text-sm text-gray-600">{t('login.feature3Desc')}</p>
              </div>
            </div>
          </div>

          <div className="text-sm text-gray-500">
            <p>© 2025 Oracle Cost Control Platform. All rights reserved.</p>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <Card className="shadow-2xl border-0">
          <CardHeader className="space-y-1 pb-6">
            <div className="flex justify-center mb-4 md:hidden">
              <img 
                src="https://logos-world.net/wp-content/uploads/2020/09/Oracle-Logo.png" 
                alt="Oracle" 
                className="h-12 w-auto"
              />
            </div>
            <CardTitle className="text-2xl font-bold text-center">{t('login.welcome')}</CardTitle>
            <CardDescription className="text-center">
              {t('login.subtitle')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('login.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="email"
                    type="email"
                    placeholder="demo@virtualcfo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    data-testid="email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">{t('login.password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input type="checkbox" className="rounded border-gray-300" />
                  {t('login.rememberMe')}
                </label>
                <Link to="/forgot-password" className="text-sm text-blue-600 hover:underline">
                  {t('login.forgotPassword')}
                </Link>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? t('common.loading') : t('login.signIn')}
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">{t('login.continueWith')}</span>
              </div>
            </div>

            <Button 
              variant="outline" 
              className="w-full"
              onClick={handleDemoLogin}
              disabled={loading}
              data-testid="demo-login-btn"
            >
              <Brain className="mr-2" size={18} />
              {t('login.demoLogin')}
            </Button>

            <p className="text-center text-sm text-gray-600">
              {t('login.noAccount')}{' '}
              <Link to="/register" className="text-blue-600 hover:underline font-medium">
                {t('login.signUp')}
              </Link>
            </p>

            <p className="text-center text-xs text-gray-500">
              {t('login.termsText')}{' '}
              <a href="#" className="text-blue-600 hover:underline">{t('login.termsService')}</a>
              {' '}{t('login.and')}{' '}
              <a href="#" className="text-blue-600 hover:underline">{t('login.privacyPolicy')}</a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
